export const AppRoutes = {
  // Auth routes
  LOGIN: '/login',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD_VERIFY: '/reset-pw/verify',
  RESET_PASSWORD: '/reset-pw',
  CREATE_ACCOUNT: '/create-account',
  CREATE_ACCOUNT_PARTNER_TYPE: '/create-account/partner-type',
  CREATE_ACCOUNT_BUSINESS_DETAILS: '/create-account/business-details',
  CREATE_ACCOUNT_VERIFY_EMAIL: '/create-account/verify-email',
  CREATE_ACCOUNT_VERIFY_PHONE: '/create-account/verify-phone',
  CREATE_ACCOUNT_DOCUMENTS: '/create-account/documents',
  CREATE_ACCOUNT_BANKING_DETAILS: '/create-account/banking-details',
  CREATE_ACCOUNT_APPROVAL_PENDING: '/create-account/approval-pending',

  // Main routes
  INDEX: '/',
  AI_SEARCH: '/ai-search',
  DASHBOARD: '/dashboard',
  SETTINGS: '/settings',
  POSTS: '/posts',
  CREATE_POST: '/posts/create',
  POST_DETAILS: '/posts/:id',
  PROFILE: '/profile',
  DONATIONS: '/donations',
  DONATIONS_ALL: '/donations/all',
  CONTACTS: '/contacts',
  DONORS: '/donors',
  INVOICES_ALL: '/invoices/all',
  PRODUCTS: '/products',
  PRODUCT_DETAILS: '/products/:id',
  PRODUCTS_COMPARE: '/products/compare',
  ADMIN_PRODUCTS: '/admin/products',
  ADMIN_PRODUCT_DETAILS: '/admin/products/:id',
  WISHLIST: '/wishlist',
  MY_CONVERSATIONS: '/my-conversations',
  MY_COMPARISONS: '/my-comparisons',
  ACCOUNT: '/account',
  PRODUCT_VARIANTS: '/product-variants',
  MY_PRODUCTS: '/my-products',
  MY_PRODUCT_DETAILS: '/my-products/:id',
  MY_PRODUCT_NEW: '/my-products/new',
  MY_PRODUCT_REQUEST_EDIT: '/my-products/requests/:id/edit',
  MY_PRODUCT_BULK_UPLOAD: '/my-products/bulk-upload',
  MY_PRODUCT_BULK_UPLOAD_RESULTS: '/my-products/bulk-upload/results',
  MY_PRODUCT_BULK_UPLOAD_PREVIEW: '/my-products/bulk-upload/preview/:index',
  MY_PRODUCT_EDIT: '/my-products/:id/edit',
  MY_WARRANTIES: '/my-warranties',
  CHECKOUT: '/checkout',
  PARTNERS: '/admin/partners',
  CREATE_PARTNER: '/admin/partners/create',
  PARTNER_DETAILS: '/admin/partners/:id',
  ORDERS: '/orders',
  ORDER_DETAILS: '/orders/:id',
  PURCHASE_ORDERS: '/purchase-orders',
  PURCHASE_ORDER_DETAILS: '/purchase-orders/:id',
  CONFIRM_DELIVERY: '/purchase-orders/:id/confirm-delivery',
  ADMIN_PURCHASE_ORDERS: '/admin/purchase-orders',
  ADMIN_PURCHASE_ORDER_DETAILS: '/admin/purchase-orders/:id',
  ONBOARDING: '/onboarding',
  ADMIN: '/admin',
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_ORDERS: '/admin/orders',
  ADMIN_ORDER_DETAILS: '/admin/orders/:id',
  ADMIN_DOCUMENTS: '/admin/documents',
  ADMIN_DOCUMENTS_USER: '/admin/documents/:userId',
  // Sellers manage their requests via the "Product Requests" tab on /my-products.
  // Kept as a constant so create / details routes can compose paths uniformly.
  MY_PRODUCT_REQUESTS: '/my-products?tab=requests',
  PRODUCT_REQUEST_DETAILS: '/product-requests/:id',
  ADMIN_PRODUCT_REQUEST_DETAILS: '/admin/product-requests/request/:id',
  ADMIN_PRODUCT_REQUESTS: '/admin/product-requests',
  ADMIN_PRODUCT_REQUESTS_USER: '/admin/product-requests/:userId',
  ADMIN_PRODUCT_REQUEST_REVIEW: '/admin/product-requests/review/:id',
  QUOTATIONS: '/quotations',
  QUOTATION_DETAILS: '/quotations/:id',
  LOYALTY_POINTS: '/loyalty-points',
  INBOX: '/inbox',
  ADMIN_LOYALTY_CONFIG: '/admin/loyalty-config',
  ADMIN_LOYALTY_PROMOTIONS: '/admin/loyalty-config/promotions',
  ADMIN_LOYALTY_PROMOTION_NEW: '/admin/loyalty-config/promotions/new',
  ADMIN_LOYALTY_PROMOTION_DETAIL: '/admin/loyalty-config/promotions/:id',
  ADMIN_LOYALTY_PROMOTION_EDIT: '/admin/loyalty-config/promotions/:id/edit',
  ADMIN_LOYALTY_SETTINGS: '/admin/loyalty-config/settings',
  ADMIN_LOYALTY_ANALYTICS: '/admin/loyalty-config/analytics',
  ADMIN_LOYALTY_TRANSACTION: '/admin/loyalty-config/transactions/:id',
  ADMIN_LOYALTY_REDEMPTION: '/admin/loyalty-config/redemptions/:id',
  ADMIN_CATALOG: '/admin/catalog',
  ADMIN_FEATURED_DEALS: '/admin/featured-deals',
  ADMIN_MARGIN_DASHBOARD: '/admin/margin',
  ADMIN_AI_EXTRACTIONS: '/admin/ai-extractions',
  PAYMENTS: '/payments',
  ADMIN_PAYMENTS: '/admin/payments',
  ADMIN_SUPPLIER_BILLS: '/admin/payments/suppliers/:supplierId',
  ADMIN_PAYMENT_DETAILS: '/admin/payments/:id',
  CHANGE_REQUESTS: '/change-requests',
  CHANGE_REQUEST_DETAILS: '/change-requests/:id',
  CHANGE_REQUEST_NEW_BUYER_PROFILE: '/change-requests/new/buyer-profile',
  CHANGE_REQUEST_NEW_PRODUCT: '/change-requests/new/product/:productId',
  ADMIN_CHANGE_REQUESTS: '/admin/change-requests',
  ADMIN_CHANGE_REQUESTS_USER: '/admin/change-requests/user/:userId',
  ADMIN_CHANGE_REQUEST_REVIEW: '/admin/change-requests/:id',
  WARRANTY_CLAIMS: '/warranty-claims',
  WARRANTY_CLAIM_DETAILS: '/warranty-claims/:id',
  MY_WARRANTY_CLAIMS: '/my-warranty-claims',
  MY_WARRANTY_CLAIM_DETAILS: '/my-warranty-claims/:id',
  ADMIN_ORDER_DISPUTES: '/admin/order-disputes',
  ADMIN_ORDER_DISPUTE_DETAILS: '/admin/order-disputes/:id',
  ADMIN_WARRANTY_CLAIMS: '/admin/warranty-claims',
  ADMIN_WARRANTY_CLAIM_DETAILS: '/admin/warranty-claims/:id',
  ADMIN_USERS: '/admin/users',
  ADMIN_USER_DETAILS: '/admin/users/:id',


  // Legal routes
  TERMS_AND_CONDITIONS: '/terms-and-conditions',
  PRIVACY_POLICY: '/privacy-policy',
  RETURN_POLICY: '/return-policy',
} as const;

export type AppRoutes = (typeof AppRoutes)[ keyof typeof AppRoutes ];

// Helper function to create a route with query parameters
export const createRoute = (route: AppRoutes, params?: Record<string, string>) => {
  if (!params) return route;

  const queryString = Object.entries(params)
    .map(([ key, value ]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

  return `${route}?${queryString}`;
};
